import React, { useState } from 'react'
import styled from '@emotion/styled'
import { HiExternalLink } from 'react-icons/hi'
import { copyToClipboard } from '@slerp/helpers/clipboard'
import { Section } from 'components/Common'
import { StepStatus } from 'hooks/useStep'
import {
  ActionCard,
  OutlineButton,
  PrimaryButton,
  OpenShipdayButton,
  FieldGroup,
  FieldLabel,
  SidePanelTitle,
  SidePanelList,
  SidePanelItem
} from './styles'
import { SHIPDAY_APP_URL } from '../../constants'
import StepLayout from './StepLayout'

interface WebhookStepProps {
  stepNumber: number
  webhookUrl: string
  token: string
  onContinue: () => void
  onBack: () => void
}

const WebhookStep = ({
  stepNumber,
  webhookUrl,
  token,
  onContinue,
  onBack
}: WebhookStepProps) => {
  const [copiedKey, setCopiedKey] = useState<'url' | 'token' | null>(null)

  const handleCopy = (value: string, key: 'url' | 'token') => {
    copyToClipboard(value).then((ok) => {
      if (ok) {
        setCopiedKey(key)
        setTimeout(() => setCopiedKey(null), 2000)
      } else {
        console.error('Failed to copy to clipboard')
      }
    })
  }

  return (
    <StepLayout
      sidebar={
        <>
          <SidePanelTitle>How to add your webhook to Shipday:</SidePanelTitle>
          <SidePanelList>
            <SidePanelItem>Log in to your Shipday account</SidePanelItem>
            <SidePanelItem>Go to Integrations</SidePanelItem>
            <SidePanelItem>Click the Add API Link button</SidePanelItem>
            <SidePanelItem>
              Copy the URL below and paste it into the URL field in Shipday
            </SidePanelItem>
            <SidePanelItem>
              Copy the Token below and paste it into the Token field in Shipday
            </SidePanelItem>
            <SidePanelItem>Click Add info</SidePanelItem>
          </SidePanelList>
        </>
      }
    >
      <Section
        title='Add your webhooks to Shipday'
        description='Add the following details in Shipday to complete setup'
        stepStatus={StepStatus.active}
        stepNumber={stepNumber}
      >
        <FieldGroup>
          <FieldLabel>Webhook URL</FieldLabel>
          <CopyRow>
            <CopyValue>{webhookUrl}</CopyValue>
            <CopyButton
              copied={copiedKey === 'url'}
              onClick={() => handleCopy(webhookUrl, 'url')}
            >
              {copiedKey === 'url' ? 'COPIED' : 'COPY'}
            </CopyButton>
          </CopyRow>
        </FieldGroup>
        <FieldGroup>
          <FieldLabel>Token</FieldLabel>
          <CopyRow>
            <CopyValue>{token}</CopyValue>
            <CopyButton
              copied={copiedKey === 'token'}
              onClick={() => handleCopy(token, 'token')}
            >
              {copiedKey === 'token' ? 'COPIED' : 'COPY'}
            </CopyButton>
          </CopyRow>
        </FieldGroup>
        <OpenShipdayButton
          href={SHIPDAY_APP_URL}
          target='_blank'
          rel='noopener noreferrer'
        >
          OPEN SHIPDAY <HiExternalLink />
        </OpenShipdayButton>
      </Section>
      <ActionCard>
        <OutlineButton onClick={onBack}>BACK</OutlineButton>
        <PrimaryButton onClick={onContinue}>
          I'VE ADDED THE WEBHOOK
        </PrimaryButton>
      </ActionCard>
    </StepLayout>
  )
}

const CopyRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  overflow: hidden;
`

const CopyValue = styled.span`
  flex: 1;
  padding: 8px;
  font-size: 14px;
  background: #f3f2f4;
  color: #4a4a68;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border: none;
  border-radius: 4px;
`

const CopyButton = styled.button<{ copied: boolean }>`
  width: 84px;
  height: 36px;
  justify-content: center;
  align-items: center;
  flex-shrink: 0;
  border: 1px solid #4a4a68;
  border-radius: 4px;
  background: ${({ copied }) => (copied ? '#4a4a68' : 'transparent')};
  font-size: 12px;
  font-weight: 600;
  color: ${({ copied }) => (copied ? '#ffffff' : '#4a4a68')};
  cursor: pointer;
  letter-spacing: 0.04em;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: ${({ copied }) => (copied ? '#4a4a68' : '#f3f2f4')};
  }
`

export default WebhookStep
